import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { HeaderPro } from '../../components/HeaderPro';
import { PrimaryButton } from '../../components/PrimaryButton';
import { SecondaryButton } from '../../components/SecondaryButton';
import {
  createProConnectOnboardingLink,
  getProConnectStatus,
  getProTaxUtrStatus,
  markProConnectReturn,
  setProTaxUtr,
  type ProConnectStatus,
} from '../../../lib/professional-api';
import { apiErrorMessage } from '../../../lib/api';
import { toast } from '../../../lib/toast';

/**
 * /professional/setup-payouts — single-purpose page that hands the
 * pro to Stripe's hosted Connect onboarding. Pro lands here:
 *
 *   - From the post-verify signup funnel as the first thing after
 *     email verification (so payouts are set up BEFORE the pro
 *     accepts any bookings — see Phase-4 booking-accept gate).
 *   - From the dashboard "Set up payouts" CTA if they skipped
 *     earlier or got bounced by a requirement.
 *
 * Two render states:
 *
 *   1. Default (?stripe is absent / ?stripe=refresh): show the
 *      "Continue on Stripe" intro, POST onboarding-link on click,
 *      redirect to Stripe. ?stripe=refresh is what Stripe sends back
 *      if the link expired mid-flow — same UI, different copy.
 *   2. Return (?stripe=return): customer just came back from Stripe.
 *      Fire markProConnectReturn (stamps TOS audit + re-snapshots
 *      account) then route to one of:
 *        - payouts_ready=true  → toast "Payouts ready", go to dashboard
 *        - currently_due > 0   → list the outstanding requirements,
 *                                 offer "Resolve on Stripe" CTA
 *        - disabled_reason set → show the reason, offer "Try again"
 *
 * Bank details and KYC are entered on STRIPE'S domain — never on
 * ours. This page only sends `email` to Stripe via accounts.create
 * and redirects.
 */
export default function SetupPayoutsPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const mode = params.get('stripe');
  const [loading, setLoading] = useState(false);
  const [returnInfo, setReturnInfo] = useState<ProConnectStatus | null>(null);
  const [returnLoading, setReturnLoading] = useState(mode === 'return');
  // HMRC UTR (tax data) — collected by US, separate from Stripe (Stripe holds
  // the banking; it doesn't hold a UTR). Required before continuing to Stripe,
  // and it gates payouts server-side. We only ever read a presence flag back,
  // never the value.
  const [utr, setUtr] = useState('');
  const [utrOnFile, setUtrOnFile] = useState(false);
  const [utrSaving, setUtrSaving] = useState(false);

  // Return-from-Stripe handler. Only fires when ?stripe=return.
  // Stamps the TOS-acceptance audit pair server-side and reads back
  // the freshly-snapshotted account so we can branch the UI on the
  // real Stripe status (not a cached one).
  useEffect(() => {
    if (mode !== 'return') return;
    let cancelled = false;
    (async () => {
      try {
        const res = await markProConnectReturn();
        if (cancelled) return;
        setReturnInfo(res.info);
        if (res.info.payouts_ready) {
          toast.success('Payouts set up — you can now accept bookings.');
          // Small delay so the customer reads the toast before we
          // bounce them.
          setTimeout(() => navigate('/professional/dashboard'), 800);
        }
      } catch (err) {
        if (cancelled) return;
        // Fall back to the live status endpoint so we at least show
        // SOMETHING actionable instead of a permanent spinner.
        try {
          const fallback = await getProConnectStatus();
          setReturnInfo(fallback.info);
        } catch {
          /* swallow — the user can click "Try again" below */
        }
        toast.error(
          apiErrorMessage(err, 'Could not confirm with Stripe — please try again.'),
        );
      } finally {
        if (!cancelled) setReturnLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mode, navigate]);

  // Load whether a UTR is already on file (default / refresh screens).
  useEffect(() => {
    if (mode === 'return') return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getProTaxUtrStatus();
        if (!cancelled) setUtrOnFile(!!res.has_tax_utr);
      } catch {
        /* non-fatal — the field just shows as not-yet-provided */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mode]);

  const handleSaveUtr = async () => {
    const clean = utr.replace(/\s+/g, '');
    if (!/^\d{10}$/.test(clean)) {
      toast.error('Your UTR must be exactly 10 digits.');
      return;
    }
    setUtrSaving(true);
    try {
      await setProTaxUtr(clean);
      setUtrOnFile(true);
      setUtr('');
      toast.success('UTR saved.');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not save your UTR.'));
    } finally {
      setUtrSaving(false);
    }
  };

  const handleStart = async () => {
    // Tax data must be on file before we hand off to Stripe — it also gates
    // payouts server-side, so collecting it up-front avoids a held payout.
    if (!utrOnFile) {
      toast.error('Please add your UTR before continuing to Stripe.');
      return;
    }
    setLoading(true);
    try {
      const res = await createProConnectOnboardingLink();
      if (res.url) {
        window.location.assign(res.url);
        return;
      }
      toast.error('Could not start Stripe onboarding. Please try again.');
    } catch (err) {
      toast.error(
        apiErrorMessage(err, 'Could not start Stripe onboarding.'),
      );
    } finally {
      setLoading(false);
    }
  };

  // -------- Return-screen branches --------
  if (mode === 'return') {
    if (returnLoading) {
      return (
        <Shell>
          <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[24px] mb-3">
            Finalising your payout setup…
          </h1>
          <p className="text-[#908e8e] text-[14px] mb-6">
            We're confirming with Stripe. This usually takes a few seconds.
          </p>
        </Shell>
      );
    }
    if (returnInfo?.payouts_ready) {
      return (
        <Shell>
          <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#1f7a3a] text-[24px] mb-3">
            Payouts ready
          </h1>
          <p className="text-[#3a3335] text-[14px] mb-6">
            Stripe has verified your details. You'll receive payouts to the
            bank account you set up there. Manage them any time from your
            Payouts page.
          </p>
          <PrimaryButton onClick={() => navigate('/professional/dashboard')}>
            Go to dashboard
          </PrimaryButton>
        </Shell>
      );
    }
    const currentlyDue = returnInfo?.currently_due ?? [];
    const disabledReason = returnInfo?.disabled_reason ?? null;
    return (
      <Shell>
        <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[24px] mb-3">
          A few more details needed
        </h1>
        <p className="text-[#908e8e] text-[14px] mb-4">
          Stripe still needs some information before you can receive payouts.
        </p>
        {currentlyDue.length > 0 ? (
          <ul className="text-[#3a3335] text-[13px] mb-6 list-disc pl-5 space-y-1">
            {currentlyDue.map((item) => (
              <li key={item}>{humanise(item)}</li>
            ))}
          </ul>
        ) : null}
        {disabledReason ? (
          <p className="text-[#a8071a] text-[13px] mb-6">
            Reason: {humanise(disabledReason)}
          </p>
        ) : null}
        <div className="flex gap-2">
          <PrimaryButton onClick={handleStart} disabled={loading}>
            {loading ? 'Opening Stripe…' : 'Resolve on Stripe'}
          </PrimaryButton>
          <Link to="/professional/dashboard">
            <SecondaryButton>Do this later</SecondaryButton>
          </Link>
        </div>
      </Shell>
    );
  }

  // -------- Default / refresh screen --------
  return (
    <Shell>
      <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[24px] mb-3">
        Set up payouts with Stripe
      </h1>
      <p className="text-[#3a3335] text-[14px] leading-[22px] mb-3">
        We use Stripe to pay you. You'll be redirected to Stripe's secure page
        to verify your identity and add your bank account.
      </p>
      <p className="text-[#908e8e] text-[12px] leading-[20px] mb-6">
        We never see your bank details — they live with Stripe, who handle UK
        Faster Payments and Bacs payouts on our behalf. Once you're done,
        Stripe brings you back here.
      </p>
      {mode === 'refresh' ? (
        <p className="text-[#8e2a2f] text-[12px] mb-4">
          The previous Stripe link expired — click below to start fresh.
        </p>
      ) : null}

      {/* HMRC UTR — we collect this (Stripe doesn't); stored encrypted and
          required before payouts. Separate from the Stripe handoff below. */}
      <div className="mb-6 rounded-[10px] border border-[#ede8e9] bg-[#fcfaf7] p-4">
        <label
          htmlFor="utr"
          className="block font-['DM_Sans',sans-serif] text-[#3a3335] text-[13px] font-bold mb-1"
        >
          UTR (Unique Taxpayer Reference)
        </label>
        <p className="text-[#908e8e] text-[12px] leading-[18px] mb-3">
          Your 10-digit HMRC tax reference. We store it securely (encrypted) for
          tax reporting — it's never shown publicly. Required to receive payouts.
        </p>
        {utrOnFile ? (
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1 font-['DM_Sans',sans-serif] text-[#1f7a3a] text-[13px] font-bold">
              ✓ UTR on file
            </span>
            <button
              type="button"
              onClick={() => setUtrOnFile(false)}
              className="text-[#8e2a2f] text-[12px] underline underline-offset-2"
            >
              Update
            </button>
          </div>
        ) : (
          <div className="flex gap-2">
            <input
              id="utr"
              type="text"
              inputMode="numeric"
              value={utr}
              onChange={(e) =>
                setUtr(e.target.value.replace(/\D/g, '').slice(0, 10))
              }
              placeholder="1234567890"
              maxLength={10}
              className="flex-1 h-[40px] px-3 rounded-[8px] border border-[#ede8e9] bg-white text-[14px] text-[#3a3335] outline-none focus:border-[#8e2a2f]"
            />
            <PrimaryButton onClick={handleSaveUtr} disabled={utrSaving}>
              {utrSaving ? 'Saving…' : 'Save UTR'}
            </PrimaryButton>
          </div>
        )}
      </div>

      <div className="flex gap-2">
        <PrimaryButton onClick={handleStart} disabled={loading}>
          {loading ? 'Opening Stripe…' : 'Continue on Stripe'}
        </PrimaryButton>
        <Link to="/professional/dashboard">
          <SecondaryButton>Skip for now</SecondaryButton>
        </Link>
      </div>
      {!utrOnFile ? (
        <p className="text-[#908e8e] text-[12px] mt-2">
          Add your UTR above to continue.
        </p>
      ) : null}
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <HeaderPro />
      <main className="flex-1 max-w-[640px] mx-auto px-4 py-16 w-full">
        <div className="border border-[#ede8e9] rounded-[12px] p-8 bg-white">
          {children}
        </div>
      </main>
    </div>
  );
}

// Stripe surfaces requirements via dotted machine codes ("external_account",
// "individual.dob.day"). Make them readable so the pro knows what to fix.
function humanise(code: string): string {
  return code
    .replace(/^individual\./, '')
    .replace(/_/g, ' ')
    .replace(/\./g, ' › ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
